import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import './PartnerwiseReport.css';

const PartnerwiseReport = () => {
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [reportData, setReportData] = useState([]);
  const navigate = useNavigate(); // Hook to navigate

  const formatDateForPrint = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatNumber = (num) => {
    return parseFloat(num || 0)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Fetch partners on component mount
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/partners`);
        setPartners(response.data);
      } catch (error) {
        console.error('Error loading partner data:', error);
        alert('Failed to load partner data.');
      }
    };

    fetchPartners();
  }, []);

  // Fetch loan report data based on selected partner
  const fetchReportData = async (partner) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/loans-by-partner/${partner}`
      );
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
      alert('Failed to load report data.');
    }
  };

  // Handle partner selection change
  const handlePartnerChange = (e) => {
    const partner = e.target.value;
    setSelectedPartner(partner);
    fetchReportData(partner);
  };

  // Handle back button click
  const handleBackClick = () => {
    navigate(-1); // Go back to the previous page
  };

  const handlePrint = () => {
    if (!selectedPartner || reportData.length === 0) {
      alert('Please select a partner and load data before printing.');
      return;
    }

    const printDate = new Date().toLocaleDateString('en-GB');

    const tableRows = reportData.map((loan) => ({
      account_no: loan.account_no,
      loanee_name: loan.loanee_name || '',
      date: formatDateForPrint(loan.date),
      loan_amount: formatNumber(loan.loan_amount),
      period: loan.period || '',
      total_paid: formatNumber(loan.total_paid),
      outstanding_amount: formatNumber(loan.outstanding_amount),
    }));

    const printContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Partnerwise Loan Report</title>
  <style>
    @media print {
      @page {
        size: A4 landscape;
        margin: 10mm;
      }
    }
    body {
      font-family: Arial, sans-serif;
      margin: 0;
      padding: 10px;
      font-size: 11px;
    }
    .header {
      text-align: center;
      margin-bottom: 10px;
    }
    .company-name {
      font-size: 24px;
      font-weight: bold;
      color: #d32f2f;
      margin-bottom: 5px;
    }
    .location {
      font-size: 14px;
      margin-bottom: 5px;
    }
    .report-title {
      font-size: 18px;
      font-weight: bold;
      color: #1976d2;
      margin-bottom: 10px;
    }
    .meta-info {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
      font-size: 12px;
    }
    .partner-name {
      font-weight: bold;
    }
    .print-date {
      font-weight: bold;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
      font-size: 10px;
    }
    th, td {
      border: 1px solid #000;
      padding: 4px;
      text-align: center;
      vertical-align: middle;
    }
    th {
      background-color: #f0f0f0;
      font-weight: bold;
      font-size: 10px;
    }
    .ac-no {
      width: 60px;
    }
    .name-col {
      width: 160px;
      text-align: left;
      padding: 4px;
    }
    .date-col {
      width: 80px;
    }
    .amount-col {
      width: 90px;
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="company-name">SHREE SIDDHARAMESHWAR FINANCE AND LEASING</div>
    <div class="location">Kasgi</div>
    <div class="report-title">-: General Loan Partnerwise Report :-</div>
  </div>
  
  <div class="meta-info">
    <div class="partner-name">Partner Name :- ${selectedPartner}</div>
    <div class="print-date">Print Date ${printDate}</div>
  </div>

  <table>
    <thead>
      <tr>
        <th class="ac-no">A/C No</th>
        <th class="name-col">Name</th>
        <th class="date-col">Date</th>
        <th class="amount-col">Loan Amount</th>
        <th>Period (Days)</th>
        <th class="amount-col">Amount Paid</th>
        <th class="amount-col">Amount Outstanding</th>
      </tr>
    </thead>
    <tbody>
      ${tableRows
        .map(
          (row) => `
        <tr>
          <td>${row.account_no}</td>
          <td class="name-col">${row.loanee_name}</td>
          <td class="date-col">${row.date}</td>
          <td class="amount-col">${row.loan_amount}</td>
          <td>${row.period}</td>
          <td class="amount-col">${row.total_paid}</td>
          <td class="amount-col">${row.outstanding_amount}</td>
        </tr>
      `
        )
        .join('')}
    </tbody>
  </table>
</body>
</html>
    `;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    printWindow.document.write(printContent);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="partnerwise-report-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Partnerwise Loan Report</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          {selectedPartner && reportData.length > 0 && (
            <button
              onClick={handlePrint}
              className="print-btn"
              style={{
                padding: '10px 20px',
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
              }}
            >
              Print
            </button>
          )}
          <button onClick={handleBackClick} className="back-button">
            Back
          </button>
        </div>
      </div>

      <div className="input-group">
        <label htmlFor="partner">Partner Name:</label>
        <select
          id="partner"
          value={selectedPartner}
          onChange={handlePartnerChange}
        >
          <option value="">Select Partner</option>
          {partners.map((partner, index) => (
            <option key={index} value={partner}>
              {partner}
            </option>
          ))}
        </select>
      </div>

      {reportData.length > 0 && (
        <table className="report-table">
          <thead>
            <tr>
              <th>Account Number</th>
              <th>Name</th>
              <th>Date</th>
              <th>Loan Amount</th>
              <th>Period (Days)</th>
              <th>Amount Paid</th>
              <th>Amount Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {reportData.map((loan) => (
              <tr key={loan.account_no}>
                <td>{loan.account_no}</td>
                <td>{loan.loanee_name}</td>
                <td>{new Date(loan.date).toLocaleDateString()}</td>
                <td>{Number(loan.loan_amount || 0).toFixed(2)}</td>
                <td>{loan.period}</td>
                <td>{Number(loan.total_paid || 0).toFixed(2)}</td>
                <td>{Number(loan.outstanding_amount || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
export default PartnerwiseReport;
